import { all, call, put, select, take, takeLatest } from 'redux-saga/effects';
import { getScribeById } from 'JS/sagas/selectors';
import { appServices } from 'JS/shared/helpers/app-services/AppServices';
import {
  loadCursors,
  loadScribeById,
  LOAD_CURSORS_SUCCESS,
  LOAD_SCRIBES_SUCCESS,
  SAVE_AUDIO_FAILED,
  SAVE_AUDIO_SUCCESS,
  UPDATE_SCRIBE_SUCCESS
} from 'JS/actionCreators/scribeActions';
import {
  getScribeForPlaybackError,
  getScribeForPlaybackSuccess,
  GET_SCRIBE_FOR_RENDER,
  PLAY_FROM_EDITOR
} from 'JS/actionCreators/playbackActions';
import { getUrlProviderForContentType } from 'JS/shared/providers';
import { sendErrorToSentry } from 'JS/logging';
import { canvasSizes, resolution } from 'JS/config/defaults';
import getCursorsResources from 'JS/sagas/sagaHelpers/getCursorsResources';
import getCursorTimings from 'JS/sagas/sagaHelpers/getCursorTimings';

function getViewportData(canvasSize = 'landscape') {
  const { width, height } = canvasSizes[canvasSize];
  return {
    width,
    height,
    resolution
  };
}

export function* getScribeForPlayback({ scribeId, isRender = false }) {
  try {
    // If we are recording audio when the user starts a playback via a click then we should wait until the audio has finished
    // processing before we continue
    const isAudioRecording = yield select(state => state.ui.isAudioPanelBusy);
    if (isAudioRecording) {
      const saveAudioAction = yield take([SAVE_AUDIO_SUCCESS, SAVE_AUDIO_FAILED]);
      if (saveAudioAction.type === SAVE_AUDIO_SUCCESS) {
        yield take(UPDATE_SCRIBE_SUCCESS);
      }
    }

    // Using JSON parse/stringify here as the scribe at this point the scribe is a JS Class Instance. We want to make sure we're passing only
    // data to the react component so using this method to get it. Lodash clonedeep will just give us a new class instance which is why it's not used.
    let scribeData = yield select(state => JSON.parse(JSON.stringify(getScribeById(state, scribeId))));

    scribeData.cursorData = {
      resourcesAndMetadata: yield call(getCursorsResources, scribeData),
      timings: getCursorTimings(scribeData)
    };
    scribeData.viewport = getViewportData(scribeData.canvasSize);

    if (scribeData.audioClips?.length !== 0 && !isRender) {
      for (const clip of scribeData.audioClips) {
        const audioUrl = yield call(appServices.getAssetUrl, clip.assetId);
        clip.url = audioUrl;
      }
    }

    const images = scribeData.elements
      ?.filter(el => el.type === 'Image')
      .map(el => {
        const { image } = el;
        return image;
      });

    if (images.length) {
      const imageUrls = yield all(
        images.reduce(
          (acc, image) => ({
            ...acc,
            [image.assetId]: call(
              getUrlProviderForContentType({ provider: image.provider, contentType: image.contentType }),
              image.assetId,
              false,
              null,
              image.recolor
            )
          }),
          {}
        )
      );

      scribeData.elements.forEach(el => {
        if (el.type !== 'Image') return;
        el.image.url = imageUrls[el.image.assetId];
      });
    }

    yield put(getScribeForPlaybackSuccess(scribeData));
  } catch (error) {
    console.error(error);
    sendErrorToSentry(error);
    yield put(getScribeForPlaybackError(error.message));
  }
}

export function* getScribeForRender({ scribeId }) {
  yield all([put(loadScribeById(scribeId, false)), put(loadCursors())]);
  yield all([take(LOAD_SCRIBES_SUCCESS), take(LOAD_CURSORS_SUCCESS)]);
  yield call(getScribeForPlayback, { scribeId, isRender: true });
}

export default function* playbackSagas() {
  yield takeLatest(PLAY_FROM_EDITOR, getScribeForPlayback);
  yield takeLatest(GET_SCRIBE_FOR_RENDER, getScribeForRender);
}
